<template>
  <div class="work-set-check-group-schema-handbook">
    <PageTitle hideMarginLeft>
      Схема приемки работ
    </PageTitle>
    <div class="tw-pt-4">
      <div class="filters row q-gutter-md q-mb-md">
        <div class="filter-item">
          <CustomSelect
            label="Тип проекта"
            placeholder="Все типы"
            clearable
            :options="projectTypesOptions"
            option-value-key="project_type_id"
            option-label-key="project_type_name"
            :value="filters.project_type_id"
            @change="(val) => onFilterChange('project_type_id', val)"
          />
        </div>
        <div class="filter-item">
          <CustomSelect
            label="Группа работ"
            placeholder="Все группы"
            clearable
            :options="workSetCheckGroupsOptions"
            option-value-key="work_set_check_group_id"
            option-label-key="work_set_check_group_name"
            :value="filters.work_set_check_group_id"
            :loading="isGroupsLoading"
            @change="(val) => onFilterChange('work_set_check_group_id', val)"
          />
        </div>
        <div class="filter-item">
          <CustomSelect
            label="Схема"
            placeholder="Все схемы"
            clearable
            :options="schemaOptions"
            option-value-key="schema_id"
            option-label-key="schema_name"
            :value="filters.schema_id"
            @change="(val) => onFilterChange('schema_id', val)"
          />
        </div>
      </div>

      <q-linear-progress
        v-if="isProjectTypesLoading || isGroupsLoading || isSchemasLoading"
        indeterminate
        class="tw-mb-4"
      />

      <div v-else class="table-wrapper">
        <CustomTable
          :rowsData="rowsData"
          :columns="workSetCheckGroupSchemaColumns"
          row-key="work_set_check_group_schema_id"
          :loading="isFetching"
          :show-search="false"
          :custom-body="true"
          :add-btn="true"
          :on-add-click="onAddClick"
          :stickyHeader="false"
        >
          <template #top-right>
            <CustomIcon
              icon="refresh"
              title="Обновить"
              :loading="isFetching"
              class="tw-mr-2"
              @click="loadData"
            />
          </template>
          <template #customBody="props">
            <q-tr>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.project_type_name }}
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.work_set_check_group_name }}
              </custom-table-cell>
              <custom-table-cell align="right" vertical-align="center">
                {{ props.row.schema_id }}
              </custom-table-cell>
              <custom-table-cell
                align="center"
                vertical-align="center"
                style="width: 120px; min-width: 120px;"
              >
                <div class="tw-flex tw-justify-center tw-items-center tw-gap-2">
                  <CustomIcon
                    icon="edit"
                    title="Изменить"
                    @click="onEditClick(props.row)"
                  />
                  <CustomIcon
                    icon="trash"
                    title="Удалить"
                    :approve-mode="true"
                    :approve="() => onDeleteClick(props.row)"
                  />
                </div>
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
        <NotFoundText v-if="!isFetching && rowsData.length === 0" />
      </div>
    </div>
  </div>
</template>

<script setup>
import PageTitle from "@/components/common/PageTitle.vue";
import CustomSelect from "@/components/form/CustomSelect";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import NotFoundText from "@/components/common/NotFoundText";
import { useQuery } from "@tanstack/vue-query";
import { ref, reactive, watch, computed } from "vue";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { MODAL_NAMES } from "@/constants";
import {
  getProjectTypes,
  getWorkSetCheckGroups,
  getWorkSetCheckGroupsByProjectType,
  getBpmnSchemas,
  getWorkSetCheckGroupSchemas,
  modifyWorkSetCheckGroupSchema,
  deleteWorkSetCheckGroupSchema,
  workSetCheckGroupSchemaColumns,
} from "./services";
import { getSchemas } from "@/views/WorkCheckBpmn/services/services";

const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();

const filters = reactive({
  project_type_id: null,
  work_set_check_group_id: null,
  schema_id: null,
});

function normalizeOptionalId(val) {
  if (val === undefined || val === null || val === "") return null;
  return val;
}

const projectTypeIdForGroups = computed(() =>
  normalizeOptionalId(filters.project_type_id)
);

const { data: projectTypesData, isLoading: isProjectTypesLoading } = useQuery({
  queryKey: ["projectTypesWorkSetCheckGroupSchema"],
  queryFn: getProjectTypes,
  refetchOnWindowFocus: false,
});

const { data: workSetCheckGroupsAllData, isLoading: isGroupsAllLoading } =
  useQuery({
    queryKey: ["workSetCheckGroupsWorkSetCheckGroupSchema", "all"],
    queryFn: getWorkSetCheckGroups,
    enabled: computed(() => projectTypeIdForGroups.value == null),
    refetchOnWindowFocus: false,
  });

const { data: workSetCheckGroupsByTypeData, isLoading: isGroupsByTypeLoading } =
  useQuery({
    queryKey: computed(() => [
      "workSetCheckGroupsWorkSetCheckGroupSchema",
      "byType",
      projectTypeIdForGroups.value,
    ]),
    queryFn: () =>
      getWorkSetCheckGroupsByProjectType(projectTypeIdForGroups.value),
    enabled: computed(() => projectTypeIdForGroups.value != null),
    refetchOnWindowFocus: false,
  });

const workSetCheckGroupsData = computed(() =>
  projectTypeIdForGroups.value != null
    ? workSetCheckGroupsByTypeData.value
    : workSetCheckGroupsAllData.value
);

const isGroupsLoading = computed(() =>
  projectTypeIdForGroups.value != null
    ? isGroupsByTypeLoading.value
    : isGroupsAllLoading.value
);

const { data: bpmnSchemasData, isLoading: isSchemasLoading } = useQuery({
  queryKey: ["wcheckSchemaTabWorkSetCheckGroupSchema"],
  queryFn: getSchemas,
  refetchOnWindowFocus: false,
});

const projectTypesOptions = ref([]);
const workSetCheckGroupsOptions = ref([]);
const schemaOptions = ref([]);

watch(
  projectTypesData,
  (val) => {
    projectTypesOptions.value = Array.isArray(val) ? val : val?.data ?? [];
  },
  { immediate: true }
);
watch(
  workSetCheckGroupsData,
  (val) => {
    workSetCheckGroupsOptions.value = Array.isArray(val) ? val : val?.data ?? [];
  },
  { immediate: true }
);
watch(
  bpmnSchemasData,
  (val) => {
    schemaOptions.value = Array.isArray(val) ? val : val?.data ?? [];
  },
  { immediate: true }
);

const rowsData = ref([]);
const isFetching = ref(false);

async function loadData() {
  isFetching.value = true;
  const res = await getWorkSetCheckGroupSchemas({
    project_type_id: normalizeOptionalId(filters.project_type_id) ?? undefined,
    work_set_check_group_id:
      normalizeOptionalId(filters.work_set_check_group_id) ?? undefined,
    schema_id: normalizeOptionalId(filters.schema_id) ?? undefined,
  });
  isFetching.value = false;
  rowsData.value = res ?? [];
}

watch(
  () => [
    filters.project_type_id,
    filters.work_set_check_group_id,
    filters.schema_id,
  ],
  () => loadData(),
  { immediate: true }
);

function onFilterChange(key, value) {
  filters[key] = normalizeOptionalId(value);
  if (key === "project_type_id") {
    filters.work_set_check_group_id = null;
  }
}

function openFormModal(editingRow = null) {
  appStore.showModal(MODAL_NAMES.AddWorkSetCheckGroupSchema, {
    title: editingRow ? "Изменить связь" : "Добавить связь",
    editingRow,
    projectTypesOptions: projectTypesOptions.value,
    schemaOptions: schemaOptions.value,
    onSubmit: async (body) => {
      const res = await modifyWorkSetCheckGroupSchema(body);
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

function onAddClick() {
  openFormModal(null);
}

function onEditClick(row) {
  openFormModal(row);
}

async function onDeleteClick(row) {
  const res = await deleteWorkSetCheckGroupSchema(
    row.work_set_check_group_schema_id
  );
  if (!res) return;
  showSucceedSnackbar();
  await loadData();
}
</script>

<style lang="scss" scoped>
.work-set-check-group-schema-handbook {
  min-height: 400px;
}

.filters {
  .filter-item {
    min-width: 280px;
  }
}

.table-wrapper {
  padding: 0;
}
</style>
